import { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import { Icon } from '../../../shared/Icon';
import { CSharpDialog } from './CSharpDialog';

import { toHTMLDateWithoutTime } from '../../../utils/dates';

import { Gift } from './types';

import { generateCSharpCode } from './giftFormUtils';

interface Props {
    gift: Gift;
    idLocked: boolean;
    onChange: (patch: Partial<Gift>) => void;
}

export const AdvancedOptions = ({ gift, idLocked, onChange }: Props) => {
    const [generatedCode, setGeneratedCode] = useState<string>();

    return (
        <>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '4px !important',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={<Icon name="expand_more" />}
                    sx={{
                        minHeight: 42,
                        '& .MuiAccordionSummary-content': { my: 1 },
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Advanced options
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Stack spacing={1.5}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                        >
                            <TextField
                                value={gift.id ?? ''}
                                size="small"
                                onChange={(event) =>
                                    onChange({ id: event.target.value })
                                }
                                label="ID"
                                disabled={idLocked}
                                fullWidth
                            />
                            <TextField
                                label="Minimum required version"
                                placeholder="e.g. 1.133"
                                value={gift.minimumVersion ?? ''}
                                onChange={(event) =>
                                    onChange({
                                        minimumVersion:
                                            event.target.value || null,
                                    })
                                }
                                slotProps={{
                                    htmlInput: { pattern: '(\\d+\\.)*\\d+$' },
                                }}
                                size="small"
                                fullWidth
                            />
                        </Stack>
                        <TextField
                            label="Gift date"
                            type="date"
                            value={toHTMLDateWithoutTime(gift.startDate)}
                            size="small"
                            fullWidth
                            slotProps={{ input: { readOnly: true } }}
                        />
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            sx={{ alignItems: { sm: 'center' } }}
                        >
                            <FormControlLabel
                                control={<Checkbox />}
                                checked={gift.defaultClaimed ?? false}
                                onChange={(_, checked) =>
                                    onChange({ defaultClaimed: checked })
                                }
                                label="Unlocked for all"
                            />
                            <FormControlLabel
                                control={<Checkbox />}
                                checked={gift.tutorial ?? false}
                                onChange={(_, checked) =>
                                    onChange({ tutorial: checked })
                                }
                                label="Tutorial Gift"
                            />
                            {gift.tutorial && gift.id && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                        setGeneratedCode(
                                            generateCSharpCode(gift),
                                        )
                                    }
                                >
                                    Generate C#
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </AccordionDetails>
            </Accordion>
            <CSharpDialog
                open={generatedCode !== undefined}
                code={generatedCode ?? ''}
                onClose={() => setGeneratedCode(undefined)}
            />
        </>
    );
};
